/**
 * Requests hub — single tabbed surface folding the 11E.5 reschedule/cancel/
 * unenroll approval queue and the 11E.6 leave approval queue together
 * (Prompt 11F.6), so the sidebar carries one "Requests" entry instead of
 * two. Extensible: a future request type is another entry in `tabs` below,
 * never a new top-level nav item.
 *
 * RBAC stays exactly what it was pre-merge — this page does not widen
 * access. The reschedule tab is always rendered (its own endpoint already
 * scopes a 'staff' caller to their own students server-side). The Leave
 * approvals tab only exists when `renderLeaveApprovalsTab` is supplied,
 * which the composition root (`src/routes/requestsHub.tsx`) does only for
 * owner/admin/manager — mirroring the backend's `_ADMIN_ROLES` gate on
 * `GET /v1/leaves` exactly. A 'staff' caller never sees the tab at all,
 * not merely a disabled one.
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabPanel } from '@/components/common';
import { useRequestQueue } from '@/features/parent-portal/api/requests';
import ApprovalQueuePage from './ApprovalQueuePage';

export type RequestsHubTabId = 'reschedules' | 'leaves';

export interface RequestsHubPageProps {
  renderLeaveApprovalsTab?: () => React.ReactNode;
  /** Pending count for the Leave approvals tab badge — computed by the
   * composition root, which is the only place both 11E.5 and 11E.6 data
   * may legally meet (features may not import each other). */
  leavePendingCount?: number;
}

const RequestsHubPage: React.FC<RequestsHubPageProps> = ({
  renderLeaveApprovalsTab,
  leavePendingCount = 0,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const showLeaves = Boolean(renderLeaveApprovalsTab);
  const requestedTab = searchParams.get('tab');
  const activeTab: RequestsHubTabId =
    requestedTab === 'leaves' && showLeaves ? 'leaves' : 'reschedules';

  // Same query key `ApprovalQueuePage`'s own default filter uses — react
  // query dedupes rather than double-fetching.
  const { data: reschedules } = useRequestQueue('pending');

  const tabs = [
    {
      id: 'reschedules',
      label: 'Reschedule / Cancel / Unenroll',
      badge: reschedules?.total || undefined,
    },
    ...(showLeaves
      ? [{ id: 'leaves', label: 'Leave approvals', badge: leavePendingCount || undefined }]
      : []),
  ];

  const handleChange = (tabId: string) => {
    setSearchParams(tabId === 'reschedules' ? {} : { tab: tabId }, { replace: true });
  };

  return (
    <div data-testid="candeza-requests-hub-page">
      <Tabs tabs={tabs} activeTab={activeTab} onChange={handleChange}>
        <TabPanel value="reschedules" activeValue={activeTab}>
          <ApprovalQueuePage />
        </TabPanel>
        {showLeaves && (
          <TabPanel value="leaves" activeValue={activeTab}>
            {renderLeaveApprovalsTab?.()}
          </TabPanel>
        )}
      </Tabs>
    </div>
  );
};

export default RequestsHubPage;
